import { useState } from 'react';
import {
  Divider,
  Table,
  Space,
  Button,
  Tooltip,
  Dropdown,
  Tag,
  Typography,
} from 'antd';
import {
  FormOutlined,
  LinkOutlined,
  DownOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { usePatientStore, useModalStore } from '../store';
import Header from './Layout/Header';
import LinkPatientModal from './Modal/LinkPatientModal';
import { MODAL_TYPES } from '../constants';
import { parseFieldSetup } from '../utils/stringUtils';

dayjs.extend(utc);
const { Text } = Typography;

const HistoryPage = ({ userInfo, messageApi }) => {
  const {
    validPatients,
    isFetchingPatients,
    onSelectPatient,
    selectedPatient,
  } = usePatientStore((state) => ({
    validPatients: state.validPatients,
    isFetchingPatients: state.isFetchingPatients,
    onSelectPatient: state.onSelectPatient,
    selectedPatient: state.selectedPatient,
  }));

  const { updateModalStatus } = useModalStore((state) => ({
    updateModalStatus: state.updateModalStatus,
  }));

  const [, setSelectedRecord] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const showModal = (record) => {
    onSelectPatient(record);
    setSelectedRecord(record);
    setIsOpen(true);
  };

  const handlePatientIconClick = (record) => {
    onSelectPatient(record);
    updateModalStatus({ open: true, type: MODAL_TYPES.PATIENT_FORM });
  };

  const handleMenuClick = (e) => {
    console.log('click', e);
  };

  const items = [];
  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const HISTORY_TABLE_COLUMNS = [
    {
      title: 'Start time',
      key: 'createTime',
      render: (_, record) => (
        <span>
          {record?.createTime
            ? dayjs(record?.createTime).utc().format('DD MMM YYYY HH:mm:ss')
            : ''}
        </span>
      ),
      sorter: (a, b) => dayjs(a.createTime).unix() - dayjs(b.createTime).unix(),
    },
    {
      title: 'Sitecode',
      key: 'sitecode',
      sorter: (a, b) =>
        parseFieldSetup(a.sitecode)
          ?.charAt(0)
          ?.localeCompare(parseFieldSetup(b.sitecode)?.charAt(0)) ?? -1,
      sortDirections: ['ascend', 'descend'],
      render: (_, record) => <span>{parseFieldSetup(record?.sitecode)}</span>,
    },
    {
      title: 'Patient name',
      key: 'patientFullName',
      render: (_, record) => (
        <span>{`${record?.patientFirstName ? ' ' + parseFieldSetup(record.patientFirstName) : ''}${record?.patientLastName ? ' ' + parseFieldSetup(record.patientLastName) : ''}`}</span>
      ),
      sorter: (a, b) =>
        parseFieldSetup(a.patientFirstName)
          ?.charAt(0)
          ?.localeCompare(parseFieldSetup(b.patientFirstName)?.charAt(0)) ?? -1,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'DOB',
      key: 'dob',
      width: 120,
      render: (_, record) => (
        <span>
          {record?.DOB ? dayjs(record?.DOB).utc().format('DD MMM YYYY') : ''}
        </span>
      ),
    },
    {
      title: 'From number',
      key: 'PHNumber',
      width: 180,
      render: (_, record) =>
        record?.PHNumber && parseFieldSetup(record?.PHNumber) ? (
          <Text copyable>{parseFieldSetup(record.PHNumber)}</Text>
        ) : (
          <span>{parseFieldSetup(record?.PHNumber)}</span>
        ),
    },
    {
      title: 'Doctor name',
      key: 'DRName',
      render: (_, record) => <span>{parseFieldSetup(record?.DRName)}</span>,
    },
    {
      title: 'Urgency',
      key: 'callType',
      width: 80,
      render: (_, record) => {
        const callType = parseFieldSetup(record?.callType);
        return (
          <span>
            {callType === 'Urgent' ? (
              <Tag color="red">Urgent</Tag>
            ) : (
              callType && <Tag color="green">{callType ?? ''}</Tag>
            )}
          </span>
        );
      },
    },
    {
      title: 'Call Type',
      key: 'callVideoVoiceType',
      width: 100,
      render: (_, record) => {
        const validCallType = record?.callSid && record?.taskSid;
        const isVoiceCall = record.callSid !== record.taskSid;
        return (
          <span>
            {validCallType &&
              (isVoiceCall ? (
                <Tag icon={<PhoneOutlined />} color="#4eb5b0">
                  Voice
                </Tag>
              ) : (
                <Tag icon={<VideoCameraOutlined />} color="#6f86d6">
                  Video
                </Tag>
              ))}
          </span>
        );
      },
      filters: [
        {
          text: 'Voice',
          value: 'voice',
        },
        {
          text: 'Video',
          value: 'website',
        },
      ],
      onFilter: (value, record) => {
        const validCallType = record?.callSid && record?.taskSid;
        const isVoiceCall = record.callSid !== record.taskSid;
        if (validCallType && value === 'voice' && isVoiceCall) return true;
        if (validCallType && value === 'website' && !isVoiceCall) return true;
        return false;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        return (
          <Space size="small">
            <Tooltip title="Open patient form">
              <Button
                shape="circle"
                icon={<FormOutlined />}
                onClick={() => handlePatientIconClick(record)}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Link to patient">
              <Button
                shape="circle"
                icon={<LinkOutlined />}
                onClick={() => showModal(record)}
                size="small"
              />
            </Tooltip>
            <Dropdown menu={menuProps}>
              <Button size="small" disabled>
                <Space>
                  Actions
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </Space>
        );
      },
      fixed: 'right',
    },
  ];

  return (
    <>
      <Header />
      <Divider>History Calls</Divider>
      <Table
        bordered
        size="small"
        rowKey={(record) => `${record.caseID} ${record.callSid}`}
        onRow={(record, index) => ({
          style: {
            background: index % 2 === 0 ? 'default' : '#FCFCFC',
          },
        })}
        loading={isFetchingPatients}
        dataSource={validPatients}
        columns={HISTORY_TABLE_COLUMNS}
        pagination={{
          pageSize: 10,
          size: 'small',
          position: ['bottomRight'],
          showSizeChanger: false,
        }}
        sticky={{
          offsetHeader: 0,
        }}
        scroll={{ y: 'calc(100vh - 220px)' }}
      />
      <LinkPatientModal
        isOpen={isOpen}
        messageApi={messageApi}
        userInfo={userInfo}
        setIsOpen={setIsOpen}
        selectedPatient={selectedPatient}
      />
    </>
  );
};

export default HistoryPage;
